import { createServerFn } from "@tanstack/react-start"
import * as z from "zod"
import { forgetDashboardUser } from "../../components/users/dashboard-current-user-role"
import { requireDashboardDeveloper, requireDashboardPermission } from "../dashboard-permissions.server"
import { requireDashboardSession } from "../dashboard-server-auth"
import { assertNotSelfAccessChange } from "./self-access-guard"
import {
  createDashboardRoleData,
  createDashboardUserData,
  deleteDashboardRoleData,
  disableDashboardUserData,
  getDashboardUserData,
  listDashboardRolesData,
  listDashboardUsersData,
  permanentlyDeleteDashboardUserData,
  resetDashboardUserMfaData,
  sendDashboardUserEmailVerificationData,
  sendDashboardUserPasswordResetData,
  updateDashboardRoleData,
  updateDashboardUserData,
} from "./users-service.server"
import {
  createDashboardRoleSchema,
  createDashboardUserSchema,
  dashboardRoleKeySchema,
  updateDashboardRoleSchema,
  updateDashboardUserSchema,
} from "./users-validation"

const listUsersInputSchema = z.object({
  page: z.number().int().min(1).optional(),
  pageSize: z.number().int().min(1).max(100).optional(),
  search: z.string().optional(),
  statusFilter: z.enum(["all", "active", "disabled"]).optional(),
  roleFilter: z.union([z.literal("all"), dashboardRoleKeySchema]).optional(),
})

const createUserInputSchema = createDashboardUserSchema.extend({
  locale: z.enum(["en", "ja"]).optional(),
})

const updateUserInputSchema = z.object({
  id: z.string().min(1),
  ...updateDashboardUserSchema.shape,
})

const deleteUserInputSchema = z.object({
  id: z.string().min(1),
})

const passwordResetInputSchema = z.object({
  id: z.string().min(1),
  locale: z.enum(["en", "ja"]).optional(),
})

const listRolesInputSchema = z.object({})

const createRoleInputSchema = createDashboardRoleSchema

const updateRoleInputSchema = updateDashboardRoleSchema

type ListUsersInput = z.infer<typeof listUsersInputSchema>

export const listDashboardUsers = createServerFn({ method: "GET" })
  .validator((input: unknown) => listUsersInputSchema.parse(input ?? {}))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardPermission(user, "playbrain.users", "read")
    return listDashboardUsersData(data as ListUsersInput)
  })

export const createDashboardUser = createServerFn({ method: "POST" })
  .validator((input: unknown) => createUserInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardPermission(user, "playbrain.users", "create")
    return createDashboardUserData(data)
  })

export const updateDashboardUser = createServerFn({ method: "POST" })
  .validator((input: unknown) => updateUserInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardPermission(user, "playbrain.users", "write")
    // Writing a user was the only check, so anyone able to edit users could
    // grant themselves any level on any feature and reload into it.
    const current = await getDashboardUserData(data.id)
    assertNotSelfAccessChange({
      actorId: user.sub,
      currentGrants: current.accessGrants ?? [],
      submittedGrants: data.accessGrants ?? [],
      targetId: data.id,
    })
    const updated = await updateDashboardUserData(data)
    forgetDashboardUser(data.id)
    return updated
  })

export const deleteDashboardUser = createServerFn({ method: "POST" })
  .validator((input: unknown) => deleteUserInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardPermission(user, "playbrain.users", "archive")
    await disableDashboardUserData(data.id)
    forgetDashboardUser(data.id)
  })

export const disableDashboardUser = deleteDashboardUser

export const permanentlyDeleteDashboardUser = createServerFn({ method: "POST" })
  .validator((input: unknown) => deleteUserInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardPermission(user, "playbrain.users", "delete")
    await permanentlyDeleteDashboardUserData(data.id)
    forgetDashboardUser(data.id)
  })

export const sendDashboardUserPasswordReset = createServerFn({ method: "POST" })
  .validator((input: unknown) => passwordResetInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardPermission(user, "playbrain.users", "write")
    await sendDashboardUserPasswordResetData(data.id, data.locale)
  })

export const sendDashboardUserEmailVerification = createServerFn({ method: "POST" })
  .validator((input: unknown) => passwordResetInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardPermission(user, "playbrain.users", "write")
    await sendDashboardUserEmailVerificationData(data.id, data.locale)
  })

export const resetDashboardUserMfa = createServerFn({ method: "POST" })
  .validator((input: unknown) => passwordResetInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardPermission(user, "playbrain.users", "write")
    await resetDashboardUserMfaData(data.id)
  })

/**
 * Listing roles is allowed for user managers as well as role managers.
 *
 * A grant names a role, so this list is what the access editor offers. Admin
 * deliberately holds no `playbrain.roles` permission, which left that editor
 * empty and pointed the user at a Roles page the same permissions forbid.
 * Managing roles still requires `playbrain.roles`; only reading is widened.
 */
export const listDashboardRoles = createServerFn({ method: "GET" })
  .validator((input: unknown) => listRolesInputSchema.parse(input ?? {}))
  .handler(async () => {
    const user = await requireDashboardSession()
    try {
      await requireDashboardPermission(user, "playbrain.roles", "read")
    } catch {
      await requireDashboardPermission(user, "playbrain.users", "read")
    }
    return listDashboardRolesData(user)
  })

export const createDashboardRole = createServerFn({ method: "POST" })
  .validator((input: unknown) => createRoleInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardDeveloper(user)
    await requireDashboardPermission(user, "playbrain.roles", "create")
    const created = await createDashboardRoleData(data, user)
    forgetDashboardUser()
    return created
  })

export const updateDashboardRole = createServerFn({ method: "POST" })
  .validator((input: unknown) => updateRoleInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardDeveloper(user)
    await requireDashboardPermission(user, "playbrain.roles", "write")
    const updated = await updateDashboardRoleData(data, user)
    forgetDashboardUser()
    return updated
  })

export const deleteDashboardRole = createServerFn({ method: "POST" })
  .validator((input: unknown) => deleteUserInputSchema.parse(input))
  .handler(async ({ data }) => {
    const user = await requireDashboardSession()
    await requireDashboardDeveloper(user)
    await requireDashboardPermission(user, "playbrain.roles", "delete")
    await deleteDashboardRoleData(data.id, user)
    forgetDashboardUser()
  })
